import React from "react";
import {
    Button,
    Card,
    CardContent,
    CardMedia,
    Dialog, DialogActions, DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Typography,
    useTheme,
} from "@mui/material";
import {Capability, useTotalStatisticsQuery, ValetudoDataPoint} from "../api";
import {useCapabilitiesSupported} from "../CapabilitiesProvider";
import PaperContainer from "../components/PaperContainer";
import LoadingFade from "../components/LoadingFade";
import {adjustColorBrightness, getFriendlyStatName, getHumanReadableStatValue} from "../utils";
import {History as HistoryIcon} from "@mui/icons-material";
import {StatisticsAchievement, statisticsAchievements} from "./res/StatisticsAchievements";
import {useIsMobileView} from "../hooks";

const achievementColors = {
    light: {
        foreground: "#ffb922",
        text: "#ffb922",
        background: "#002990"
    },
    dark: {
        foreground: "",
        text: "",
        background: ""
    },
    noAchievement: {
        foreground: "#191919",
        text: "#191919",
        background: "#333333"
    }
};
achievementColors.dark = {
    foreground: adjustColorBrightness(achievementColors.light.foreground, -20),
    text: adjustColorBrightness(achievementColors.light.foreground, -5),
    background: adjustColorBrightness(achievementColors.light.background, -20),
};

const StatisticsGridItem: React.FunctionComponent<{ dataPoint: ValetudoDataPoint}> = ({ dataPoint}): JSX.Element => {
    const [overviewDialogOpen, setOverviewDialogOpen] = React.useState(false);
    const mobileView = useIsMobileView();

    const mostRecentAchievement = statisticsAchievements[dataPoint.type].find(achievement => {
        return dataPoint.value >= achievement.value;
    });

    return (
        <>
            <Grid item xs={12} sm={4} style={{userSelect: "none"}}>
                <Card style={{height: "100%"}}>
                    <Grid container style={{height: "100%"}}>
                        <Grid
                            item
                            style={{
                                marginLeft: "auto",
                                marginRight: "auto"
                            }}
                        >
                            <CardMedia component={StatisticsAward} achievement={mostRecentAchievement}/>
                        </Grid>
                        <Grid item style={{alignSelf: "flex-end", width: "100%"}}>
                            <CardContent style={{paddingBottom: "16px"}}>
                                {<Typography variant="body1" mb={2}>
                                    {mostRecentAchievement?.description || "No achievement yet"}
                                </Typography>}

                                <Grid container>
                                    <Grid
                                        item
                                        style={{
                                            flexGrow: 3
                                        }}
                                    >
                                        <Typography sx={{fontSize: 14}} color="text.secondary" gutterBottom>
                                            {getFriendlyStatName(dataPoint)}
                                        </Typography>
                                        <Typography variant="h5" component="div">
                                            {getHumanReadableStatValue(dataPoint)}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        item
                                        style={{marginTop: "auto"}}
                                    >
                                        <IconButton
                                            onClick={() => {
                                                setOverviewDialogOpen(true);
                                            }}
                                            title="Achievement Overview"
                                        >
                                            <HistoryIcon/>
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Grid>
                    </Grid>
                </Card>
            </Grid>
            <Dialog
                open={overviewDialogOpen}
                onClose={() => {
                    setOverviewDialogOpen(false);
                }}
                fullScreen={mobileView}
            >
                <DialogTitle style={{userSelect: "none"}}>
                    {getFriendlyStatName(dataPoint)} Achievements
                </DialogTitle>

                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {[...statisticsAchievements[dataPoint.type]].reverse().map((achievement, i) => {
                            const notYetAchievedAchievement : StatisticsAchievement = {
                                value: achievement.value,
                                title: "?",
                                description: "Not yet achieved"
                            };
                            const achievementToDisplay = dataPoint.value >= achievement.value ? achievement : notYetAchievedAchievement;

                            return (
                                <Grid item xs={12} sm={4} style={{userSelect: "none"}} key={`${dataPoint.type}_overview_${i}`}>
                                    <Card style={{height: "100%"}}>
                                        <Grid container style={{height: "100%"}}>
                                            <Grid
                                                item
                                                style={{
                                                    marginLeft: "auto",
                                                    marginRight: "auto"
                                                }}
                                            >
                                                <CardMedia
                                                    component={StatisticsAward}
                                                    achievement={achievementToDisplay}
                                                    achieved={achievementToDisplay === achievement}
                                                />
                                            </Grid>
                                            <Grid item style={{alignSelf: "flex-end", width: "100%"}}>
                                                <CardContent style={{paddingBottom: "16px"}}>
                                                    {<Typography variant="body1" mb={2}>
                                                        {achievementToDisplay.description}
                                                    </Typography>}
                                                </CardContent>
                                            </Grid>
                                        </Grid>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setOverviewDialogOpen(false);
                    }}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const StatisticsAward: React.FunctionComponent<{ achievement?: StatisticsAchievement, achieved?: boolean }> = ({achievement, achieved}): JSX.Element => {
    const theme = useTheme();

    let foregroundColor;
    let textColor;
    let backgroundColor;
    let ribbonFill;


    if (achievement && achieved !== false) {
        ribbonFill = "url(#ribbonGradient)";

        if (theme.palette.mode === "light") {
            foregroundColor = achievementColors.light.foreground;
            textColor = achievementColors.light.text;
            backgroundColor = achievementColors.light.background;
        } else {
            foregroundColor = achievementColors.dark.foreground;
            textColor = achievementColors.dark.text;
            backgroundColor = achievementColors.dark.background;
        }

    } else {
        foregroundColor = achievementColors.noAchievement.foreground;
        textColor = achievementColors.noAchievement.text;
        backgroundColor = achievementColors.noAchievement.background;

        ribbonFill = "url(#ribbonGradientDisabled)";
    }

    let fontSize = 14;
    let textAnchorY = 54;

    if (achievement?.title) {
        if (achievement.title.length >= 14) {
            fontSize = 8;
            textAnchorY = 52.5;
        } else if (achievement.title.length >= 12) {
            fontSize = 10;
            textAnchorY = 53;
        } else if (achievement.title.length >= 10) {
            fontSize = 12;
            textAnchorY = 53.5;
        }
    }

    return (
        <div
            style={{
                width:"90%",
                marginLeft: "auto",
                marginRight: "auto",

                marginTop: "1rem",

                userSelect: "none"
            }}
        >
            <svg width="300" height="360" viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg" style={{width: "100%", height: "100%"}}>
                <defs>
                    <linearGradient id="ribbonGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{stopColor: "#000934", stopOpacity: 1}} />
                        <stop offset="80%" style={{stopColor: "#000b67", stopOpacity: 1}}/>
                    </linearGradient>
                    <linearGradient id="ribbonGradientDisabled" x1="0%" y1="-50%" x2="0%" y2="100%">
                        <stop offset="0%" style={{stopColor: "#111111", stopOpacity: 1}} />
                        <stop offset="80%" style={{stopColor: "#222222", stopOpacity: 1}}/>
                    </linearGradient>
                </defs>
                <g transform="matrix(.97692 0 0 .96031 -23.268 -4.3406)">
                    <path d="m45.608 69.55h58.738v59.93l-29.369-15.115-29.369 15.115z" fill={ribbonFill}/>
                    <path d="m47.655 69.55h54.69v56.233l-27.345-14.182-27.345 14.182z" fill="none" stroke={foregroundColor} strokeWidth=".93469"/>
                </g>
                <g transform="matrix(.97692 0 0 .97692 -23.269 -5.3921)" stroke={foregroundColor}>
                    <path d="m75 106.08-8.67-5.792-10.226 2.033-5.793-8.668-10.226-2.035-2.035-10.226-8.669-5.793 2.033-10.226-5.792-8.67 5.792-8.67-2.033-10.226 8.669-5.793 2.035-10.226 10.226-2.035 5.793-8.669 10.226 2.033 8.67-5.792 8.67 5.792 10.226-2.033 5.793 8.669 10.226 2.035 2.035 10.226 8.668 5.793-2.033 10.226 5.792 8.67-5.792 8.67 2.033 10.226-8.668 5.793-2.035 10.226-10.226 2.035-5.793 8.668-10.226-2.033z" fill={backgroundColor} strokeWidth="3"/>
                    <path d="m75 101.51-7.8677-5.2561-9.2797 1.8449-5.2569-7.866-9.2797-1.8467-1.8467-9.2798-7.8668-5.257 1.8449-9.2798-5.256-7.8678 5.256-7.8678-1.8449-9.2798 7.8668-5.257 1.8467-9.2798 9.2797-1.8467 5.2569-7.8669 9.2797 1.8449 7.8677-5.2561 7.8677 5.2561 9.2797-1.8449 5.2569 7.8669 9.2797 1.8467 1.8467 9.2798 7.8659 5.257-1.8449 9.2798 5.256 7.8678-5.256 7.8678 1.8449 9.2798-7.8659 5.257-1.8467 9.2798-9.2797 1.8467-5.2569 7.866-9.2797-1.8449z" fill="none" strokeWidth=".90747"/>
                </g>
                <text x="50" y="50">
                    <tspan x="50" y={textAnchorY} fill={textColor} fontSize={fontSize} textAnchor="middle">
                        {achievement?.title}
                    </tspan>
                </text>
            </svg>
        </div>
    );
};

const TotalStatisticsInternal: React.FunctionComponent = (): JSX.Element => {
    const {
        data: totalStatisticsState,
        isLoading: totalStatisticsLoading,
        isError: totalStatisticsError,
    } = useTotalStatisticsQuery();

    return React.useMemo(() => {
        if (totalStatisticsLoading) {
            return (
                <LoadingFade/>
            );
        }

        if (totalStatisticsError || !totalStatisticsState) {
            return <Typography color="error">Error loading statistics</Typography>;
        }

        const statistics = totalStatisticsState.sort((a, b) => {
            const aMapped = SORT_ORDER[a.type] ?? 10;
            const bMapped = SORT_ORDER[b.type] ?? 10;

            if (aMapped < bMapped) {
                return -1;
            } else if (bMapped < aMapped) {
                return 1;
            } else {
                return 0;
            }
        }).map((dataPoint) => {
            return <StatisticsGridItem dataPoint={dataPoint} key={dataPoint.type}/>;
        });

        return (
            <Grid container spacing={2}>
                {statistics}
            </Grid>
        );
    }, [totalStatisticsError, totalStatisticsLoading, totalStatisticsState]);
};

const TotalStatistics = (): JSX.Element => {
    const [supported] = useCapabilitiesSupported(Capability.TotalStatistics);

    return (
        <PaperContainer>
            {supported ? <TotalStatisticsInternal/> : (
                <Typography color="error">This robot does not support total statistics.</Typography>
            )}
        </PaperContainer>
    );
};

const SORT_ORDER = {
    "time": 1,
    "area": 2,
    "count": 3
};

export default TotalStatistics;
